import React from 'react';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useSidebar } from '@/context';

const LayoutWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  background: linear-gradient(135deg, 
    #FFFAF5 0%, 
    #FFF8F0 25%,
    #FFF5EB 50%,
    #FFFBF5 75%,
    #FFFFFF 100%
  );
  position: relative;
  
  /* Multiple gradient overlays for depth */
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(ellipse at 0% 0%, rgba(255, 107, 53, 0.06) 0%, transparent 40%),
      radial-gradient(ellipse at 100% 0%, rgba(255, 140, 66, 0.05) 0%, transparent 50%),
      radial-gradient(ellipse at 100% 100%, rgba(212, 175, 55, 0.04) 0%, transparent 40%),
      radial-gradient(ellipse at 0% 100%, rgba(184, 134, 11, 0.03) 0%, transparent 35%),
      radial-gradient(ellipse at 50% 50%, rgba(255, 179, 71, 0.02) 0%, transparent 60%);
    pointer-events: none;
    z-index: 0;
  }
  
  /* Subtle pattern overlay */
  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(rgba(255, 107, 53, 0.03) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none;
    z-index: 0;
    opacity: 0.5;
  }
`;

const MainContent = styled.main<{ $sidebarCollapsed: boolean }>`
  flex: 1;
  margin-left: ${({ $sidebarCollapsed }) => $sidebarCollapsed ? '80px' : '280px'};
  margin-top: 72px;
  min-height: calc(100vh - 72px);
  background: transparent;
  transition: margin-left ${({ theme }) => theme.transitions.normal};
  position: relative;
  z-index: 1;
  
  /* Top accent line - orange to gold gradient */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, 
      #FF6B35 0%, 
      #FF8C42 20%, 
      #FFB347 40%, 
      #FFD700 60%,
      #D4AF37 80%,
      #B8860B 100%
    );
    box-shadow: 0 2px 8px rgba(255, 107, 53, 0.2);
  }
  
  /* Side accent glow */
  &::after {
    content: '';
    position: absolute;
    top: 4px;
    left: 0;
    width: 200px;
    height: 300px;
    background: radial-gradient(ellipse at 0% 0%, rgba(255, 107, 53, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: 1024px) {
    margin-left: 0;
  }
  
  @media (max-width: 480px) {
    margin-top: 60px;
    min-height: calc(100vh - 60px);
      width: 100%;
  }
`;

const ContentWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 1680px;
  margin: 0 auto;
  position: relative;
  
  /* Top right corner accent */
  &::before {
    content: '';
    position: absolute;
    top: 20px;
    right: 20px;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(255, 179, 71, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
  
  /* Bottom left corner accent */
  &::after {
    content: '';
    position: absolute;
    bottom: 50px;
    left: 20px;
    width: 120px;
    height: 120px;
    background: radial-gradient(circle, rgba(212, 175, 55, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
  
  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
    
    &::before, &::after {
      display: none;
    }
  }
`;

export const MainLayout: React.FC = () => {
  const { isCollapsed } = useSidebar();

  return (
    <LayoutWrapper>
      <Sidebar />
      <Header />
      <MainContent $sidebarCollapsed={isCollapsed}>
        <ContentWrapper>
          <Outlet />
        </ContentWrapper>
      </MainContent>
    </LayoutWrapper>
  );
};

